// @givmo/app-admin — dashboard money strip (spec §127; EP-WEB9 for the utilization card).
//
// FOUR cards, re-ranked from the original program-KPI strip to the four money questions an admin
// actually logs in with: what can we spend, what did we match, what did we give, how much of the
// program is being used.
//
//   stat-balance       Wallet balance         + an Owner-gated "Add funds"
//   stat-donations     Donations matched YTD  + YoY  ← testid PINNED by an e2e spec; do not rename
//   stat-company-gave  Company gave YTD       + YoY
//   stat-utilization   Program utilization    (EP-WEB9; a Meter, never a fabricated 0%)
//
// The two displaced cards are NOT lost, they are re-homed (§129): median moves to the Participation
// panel as "Median match (YTD)", charities-supported heads the Impact panel. That re-ranking is the
// whole point — nothing from the old strip disappeared.
//
// BASES DIFFER PER CARD, which is why every one of them carries a <MethodologyHint>:
//   - `matched_ytd` / `matched_prior_ytd` are APPROVED MATCHES ONLY since the EP-WEB-B1 union split.
//     Before it they carried matches ∪ direct company gifts, so this card counted money that matched
//     nothing. Read the explicit fields; never subtract one from the other client-side.
//   - `company_gave_ytd` is the company's own giving: direct donations (including fundraiser seeds)
//     plus the GROSS charge of CARD-funded gift cards. Wallet-funded gift cards and wallet deposits
//     are excluded — both were already receipted elsewhere, so counting them would double-count.
//   - Utilization's numerator is the BUDGET-ENFORCEMENT basis (approved matches, the gate's own year
//     semantics), deliberately not `matched_ytd`. A utilization percentage that disagreed with the
//     check that actually blocks an approval is the worst number to put on this screen.
//
// PURELY PRESENTATIONAL BY DESIGN: the page owns every hook and hands this section resolved state. The
// deltas are derived here because `yoyDelta` is a pure function, not a hook.

import type { ReactNode } from 'react';
import { Button, Icon, Meter, StatCard, Skeleton } from '@givmo/design-system';
import { Gauge, HandHeart, HeartHandshake, Wallet } from 'lucide-react';
import type { CompanyDashboard } from '@givmo/api-client';
import { MethodologyHint } from '../../../components/methodology-hint';
import { useEmployerModulePaths } from '../../../lib/module-paths';
import { formatUsd, yoyDelta } from '../dashboard-decode';
import {
  utilizationBasisCaption,
  utilizationMethodology,
  utilizationPercentLabel,
  type UtilizationView,
} from '../utilization-decode';
import { ErrorCard, InlineError, errorMessage } from './section-status';

export function StatStrip({
  data,
  loading,
  isError,
  error,
  onRetry,
  walletBalance,
  walletLoading,
  canManage,
  canAddFunds,
  onAddFunds,
  utilization,
  utilizationLoading,
  utilizationIsError,
  utilizationError,
  onUtilizationRetry,
  onSetBudget,
  onInviteEmployees,
}: {
  data: CompanyDashboard | undefined;
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  /** The wallet's spendable balance, already formatted (money is a string — formatted, never parsed). */
  walletBalance: string | null;
  walletLoading: boolean;
  /** Owner gate — the page's `canManage`, including its deliberate fail-OPEN on a role-read error. */
  canManage: boolean;
  /**
   * Who may add funds — the page's `mayAddFunds` (EP-SUITE7, D-EP-50): the base-role Owner with the
   * same fail-OPEN, or a holder of `company_wallet:deposit` (a Donations Owner, by default).
   */
  canAddFunds: boolean;
  onAddFunds: () => void;
  utilization: UtilizationView | null;
  utilizationLoading: boolean;
  utilizationIsError: boolean;
  utilizationError: unknown;
  onUtilizationRetry: () => void;
  onSetBudget: () => void;
  onInviteEmployees: () => void;
}) {
  // Who adds funds, in this host's words (EP-MNT5(h)): an Owner on both hosts since EP-SUITE7 — the
  // console's Donations Owners hold the deposit string by default, as every Global Admin does.
  const { words } = useEmployerModulePaths();
  const matchedDelta = yoyDelta(data?.matched_ytd, data?.matched_prior_ytd);
  const companyGaveDelta = yoyDelta(data?.company_gave_ytd, data?.company_gave_prior_ytd);

  return (
    <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 xl:grid-cols-4">
      {/* The wallet card reads a DIFFERENT query from the other three, so it keeps its own state: a
          failed dashboard read must not blank the balance, and vice versa. */}
      {walletLoading ? (
        <StatCardSkeleton />
      ) : (
        <StatCard
          testId="stat-balance"
          label={
            <LabelWithHint subject="Wallet balance" testId="stat-balance-methodology">
              Your company wallet&apos;s spendable balance — deposited funds available right now,
              minus any amount held for an in-flight charge. It is a balance, not a match budget.
            </LabelWithHint>
          }
          // An unreadable wallet is an em-dash, never "$0": telling a company with money in the wallet
          // that it has none is the exact failure this rule exists for.
          value={walletBalance ?? '—'}
          sublabel={
            canAddFunds ? (
              <Button
                appearance="saas"
                tone="ghost"
                size="sm"
                className="-ml-8"
                onClick={onAddFunds}
                testId="stat-balance-add-funds"
              >
                Add funds
              </Button>
            ) : (
              words.balanceAskDepositor
            )
          }
          icon={<Icon icon={Wallet} size={18} aria-hidden />}
        />
      )}

      {loading ? (
        <>
          <StatCardSkeleton />
          <StatCardSkeleton />
        </>
      ) : isError ? (
        <ErrorCard
          className="sm:col-span-2"
          message={errorMessage(error, 'Failed to load dashboard stats.')}
          onRetry={onRetry}
        />
      ) : (
        <>
          <StatCard
            testId="stat-donations"
            label={
              <LabelWithHint subject="Donations matched" testId="stat-donations-methodology">
                Approved employee match requests only, for the calendar year to date
                (America/New_York boundaries). Your company&apos;s own gifts are counted separately,
                under &ldquo;Company gave&rdquo;.
              </LabelWithHint>
            }
            value={formatUsd(data?.matched_ytd)}
            delta={matchedDelta?.text}
            deltaDirection={matchedDelta?.direction}
            deltaIntent="positive"
            sublabel={`vs ${formatUsd(data?.matched_prior_ytd)} this time last year`}
            icon={<Icon icon={HandHeart} size={18} aria-hidden />}
          />
          <StatCard
            testId="stat-company-gave"
            label={
              <LabelWithHint subject="Company gave" testId="stat-company-gave-methodology">
                Your company&apos;s own giving this year to date: direct donations, fundraiser seed
                gifts, and card-funded gift cards at the amount charged. Wallet-funded gift cards
                and wallet deposits are excluded — both were already counted when that money moved.
              </LabelWithHint>
            }
            value={formatUsd(data?.company_gave_ytd)}
            delta={companyGaveDelta?.text}
            deltaDirection={companyGaveDelta?.direction}
            deltaIntent="positive"
            sublabel={`vs ${formatUsd(data?.company_gave_prior_ytd)} this time last year`}
            icon={<Icon icon={HeartHandshake} size={18} aria-hidden />}
          />
        </>
      )}

      <UtilizationCard
        view={utilization}
        loading={utilizationLoading}
        isError={utilizationIsError}
        error={utilizationError}
        onRetry={onUtilizationRetry}
        canManage={canManage}
        onSetBudget={onSetBudget}
        onInviteEmployees={onInviteEmployees}
      />
    </div>
  );
}

/**
 * Program utilization (EP-WEB9) — the canvas's "single smartest metric to steal", because it reframes a
 * budget as something to spend rather than something to protect.
 *
 * FOUR STATES, and none of them is "0%":
 *   - a real ratio                 → the Meter, with the basis NAMED on the card
 *   - no budget at all             → "set a match budget", pointing at the Match Program page
 *   - a budget but nobody enrolled → the denominator is zero; the remedy is INVITING EMPLOYEES, not
 *                                    editing the policy, so it says that and links to Invites
 *   - a failed read                → inline error with retry
 *
 * The middle two are separate states because the wire distinguishes them and the remedies differ. One
 * merged "set a budget" empty state would send an admin who already set one to go set it again.
 */
function UtilizationCard({
  view,
  loading,
  isError,
  error,
  onRetry,
  canManage,
  onSetBudget,
  onInviteEmployees,
}: {
  view: UtilizationView | null;
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  canManage: boolean;
  onSetBudget: () => void;
  onInviteEmployees: () => void;
}) {
  if (loading) return <StatCardSkeleton />;

  if (isError) {
    return (
      <div className="rounded-xl border border-border-subtle bg-surface p-20 shadow-xs">
        <InlineError
          message={errorMessage(error, 'Failed to load program utilization.')}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-12 rounded-xl border border-border-subtle bg-surface p-20 shadow-xs"
      data-testid="stat-utilization"
    >
      <div className="flex items-center justify-between gap-12">
        <span className="flex items-center gap-6 font-sans text-s13 font-w500 tracking-normal text-text-tertiary">
          Program utilization
          <MethodologyHint subject="Program utilization" testId="stat-utilization-methodology">
            {utilizationMethodology(view)}
          </MethodologyHint>
        </span>
        <span className="flex size-32 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon icon={Gauge} size={18} aria-hidden />
        </span>
      </div>

      {view === null || view.kind === 'no-budget' ? (
        <UtilizationEmpty
          message="Set a match budget to track how much of your program is being used."
          actionLabel="Set a budget"
          actionTestId="stat-utilization-set-budget"
          fallback="An Owner can set one in Match Program."
          canAct={canManage}
          onAct={onSetBudget}
        />
      ) : view.kind === 'no-employees' ? (
        <UtilizationEmpty
          message="Your budget is set per employee and nobody is enrolled yet, so there is no total to measure against."
          actionLabel="Invite employees"
          actionTestId="stat-utilization-invite"
          // Inviting is not an Owner-only action, so this state has no gated fallback.
          canAct
          onAct={onInviteEmployees}
        />
      ) : (
        <div className="flex flex-col gap-8">
          <span className="font-sans text-s30 font-w700 leading-h1 tracking-givmo text-text-strong tabular-nums">
            {utilizationPercentLabel(view)}
          </span>
          <Meter
            value={view.ratio}
            max={1}
            size="sm"
            tone="brand"
            ariaLabel="Program utilization"
            valueText={utilizationPercentLabel(view)}
            testId="stat-utilization-meter"
          />
          <p className="font-sans text-s13 tracking-normal text-text-tertiary">
            {utilizationBasisCaption(view)}
          </p>
        </div>
      )}
    </div>
  );
}

/** An empty-state-with-action inside the utilization card — never a 0% meter. */
function UtilizationEmpty({
  message,
  actionLabel,
  actionTestId,
  fallback,
  canAct,
  onAct,
}: {
  message: string;
  actionLabel: string;
  actionTestId: string;
  fallback?: string;
  canAct: boolean;
  onAct: () => void;
}) {
  return (
    <div className="flex flex-col gap-8">
      <p className="font-sans text-s14 leading-h15 tracking-normal text-text-secondary">
        {message}
      </p>
      {canAct ? (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          className="self-start"
          onClick={onAct}
          testId={actionTestId}
        >
          {actionLabel}
        </Button>
      ) : fallback !== undefined ? (
        <p className="font-sans text-s13 tracking-normal text-text-tertiary">{fallback}</p>
      ) : null}
    </div>
  );
}

/** A StatCard label with its methodology affordance beside it (§135). */
function LabelWithHint({
  subject,
  testId,
  children,
}: {
  subject: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-6">
      {subject}
      <MethodologyHint subject={subject} testId={testId}>
        {children}
      </MethodologyHint>
    </span>
  );
}

function StatCardSkeleton() {
  return (
    <div className="flex flex-col gap-12 rounded-xl border border-border-subtle bg-surface p-20 shadow-xs">
      <div className="flex items-center justify-between gap-12">
        <Skeleton className="h-12 w-24" />
        <Skeleton className="size-32 rounded-lg" />
      </div>
      <div className="flex flex-col gap-8">
        <Skeleton className="h-28 w-32" />
        <Skeleton className="h-10 w-[160px]" />
      </div>
    </div>
  );
}
